import { useState } from 'react'

import {
  AnalyticsService,
  type SimilarityAnalysisResult,
  type SimilarityConfig,
  type MergeResult,
} from '../../services/analyticsService'

export function useSimilarityAnalysis() {
  const [analysisData, setAnalysisData] =
    useState<SimilarityAnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mergeLoading, setMergeLoading] = useState(false)
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null)

  const handleAnalyze = async (config?: Partial<SimilarityConfig>) => {
    setIsLoading(true)
    setError(null)
    setMergeResult(null)

    try {
      const result = await AnalyticsService.getEmotionalTagSimilarity(config)
      setAnalysisData(result)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to analyze similarity'
      setError(errorMessage)
      console.error('Similarity analysis error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMergeConfirm = async (
    sourceTag: string,
    targetTag: string,
    callback?: () => void
  ) => {
    setMergeLoading(true)

    try {
      const result = await AnalyticsService.mergeEmotionalTags(
        sourceTag,
        targetTag
      )
      setMergeResult(result)

      // Refresh the analysis to show updated results
      setTimeout(() => {
        handleAnalyze()
        callback?.()
      }, 1000)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to merge tags'
      setError(errorMessage)
      console.error('Merge error:', error)
    } finally {
      setMergeLoading(false)
    }
  }

  const clearError = () => setError(null)
  const clearMergeResult = () => setMergeResult(null)

  // Helper functions for data formatting
  const formatSimilarityScore = (score: number) => {
    return `${(score * 100).toFixed(1)}%`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const groupedMatches = analysisData
    ? AnalyticsService.formatSimilarityMatches(analysisData.matches)
    : {}

  const mergeRecommendations = analysisData
    ? AnalyticsService.generateMergeRecommendations(analysisData.matches)
    : []

  return {
    // State
    analysisData,
    isLoading,
    error,
    mergeLoading,
    mergeResult,
    groupedMatches,
    mergeRecommendations,

    // Actions
    handleAnalyze,
    handleMergeConfirm,
    clearError,
    clearMergeResult,

    // Helpers
    formatSimilarityScore,
    formatDate,
  }
}
